import classNames from 'classnames';
import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DOWNLOAD_ONLY, getCurrentPlatformLink, getLoginRedirectLink, getSignupRedirectLink, HistoryContext, localStorageAdd, useHubSwitch, useOnClickOutside, useUserSettings, useViewport } from '@bpm-web-app/utils';
import { useRouter } from 'next/router';
import { useCurrentUser, useNotifications } from '@bpm-web-app/swr-hooks';
import { Notification } from '@bpm-web-app/supreme-api-sdk';
import { Platform } from '@bpm-web-app/api-client';
import { OnboardingModalNotifications } from '../onboarding-modals/onboarding-modal-notifications/onboarding-modal-notifications';

// Icons
import NavIcon from '../../assets/icons/nav-icon.svg';
import HomeIcon from '../../assets/icons/home.svg';
import SearchIcon from '../../assets/icons/search-icon-nav.svg';
import SearchIconActive from '../../assets/icons/search-icon-nav-active.svg';
import PlaylistIcon from '../../assets/icons/my-playlists.svg';
import CloseIcon from '../../assets/icons/close.svg';
import BPMLogo from '../../assets/icons/bpm-logo.svg';

import styles from './supreme-header.module.css';
import { NavContext } from '../nav/nav.context';
import { FiltersContext } from '../filters/filters.context';
import PredictiveSearch from '../predictive-search/predictive-search';
import { useSession } from '../shared/session-provider/session-provider';
import { UserNotifications } from '../user-notifications/user-notifications';
import { modalOpenBodyClassName } from '../onboarding-modals/modal-slider/modal-slider';
import { usePlayer } from '../player/player.context';
import { ProfileNavItems } from './profile-nav-items';
import { AppLink } from '../shared/app-link/app-link';
import { PlatformSwitch } from '../shared/platform-switch/platform-switch';
import { SearchInput } from '../shared';
import Filters from '../filters/filters';
import PredictiveSearchMobile from '../predictive-search/predictive-search-mobile';
import PlaylistsForm, { PlaylistsFormProps } from '../playlists-form/playlists-form';
import { SquaredButton } from '../shared/squared-button/squared-button';
import { ExpandNavButton } from './expand-nav-button';
import { HeaderProfileButton } from './header-profile-button/header-profile-button';
import { HeaderNotificationButton } from './header-notification-button/header-notification-button';
import { HeaderPlatformsButton } from './header-platforms-button/header-platforms-button';

/* eslint-disable-next-line @typescript-eslint/no-empty-interface */
export interface SupremeHeaderProps { }

export function SupremeHeader(props: SupremeHeaderProps) {
    const { user } = useSession();
    const { hub } = useHubSwitch();

    const isWebview = !!window.isRNWebView;

    const { goBack, goForwards, canGoBack, canGoForwards } = useContext(HistoryContext);
    const { isFiltersOpen, openFilters, closeFilters } = useContext(FiltersContext);
    const { setIsMaxiPlayer } = usePlayer();
    const router = useRouter();
    const { data: userData } = useCurrentUser();
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const { isNavOpen, isNavHidden, openNav, closeNav, isSearchVisible, hideSearchBar, showSearchBar } = useContext(NavContext);
    const { isDownload } = useHubSwitch();
    const { ref: predictiveSearchRef, showComponent, setShowComponent } = useOnClickOutside(false);

    const { data: notifications } = useNotifications(isDownload ? [Notification.PlatformEnum.Download] : [Notification.PlatformEnum.Stream]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showPlatformSwitch, setShowPlatformSwitch] = useState(false);
    const { isMobile: isMobileViewPort } = useViewport();
    const { ref: notificationElRef } = useOnClickOutside<HTMLDivElement>(false, () =>
        isMobileViewPort || document.body.classList.contains(modalOpenBodyClassName) ? null : setShowNotifications(false)
    );

    const desktopMenuOpenerRef = useRef<HTMLDivElement>(null);
    const { ref: profileMenuRef } = useOnClickOutside<HTMLDivElement>(false, (e: MouseEvent) => {
        if (!desktopMenuOpenerRef.current?.contains(e.target as Node)) {
            setIsProfileMenuOpen(false);
        }
    });

    const desktopSwitchPlatformOpenerRef = useRef<HTMLDivElement>(null);
    const { ref: switchPlatformMenuRef } = useOnClickOutside<HTMLDivElement>(false, (e: MouseEvent) => {
        if (!desktopSwitchPlatformOpenerRef.current?.contains(e.target as Node)) {
            setShowPlatformSwitch(false);
        }
    });

    const [searchTerm, setSearchTerm] = useState('');

    const hasNotifications = useMemo(() => notifications?.data?.some((notification) => !notification.read), [notifications?.data]);

    const closeIcon = useMemo(() => <CloseIcon />, []);
    const navIcon = useMemo(() => <NavIcon />, []);
    const isSearchPage = useMemo(() => router.pathname === '/search', [router.pathname]);
    const [openForm, setOpenForm] = useState<null | PlaylistsFormProps>(null);
    const { isAnonymous } = useUserSettings();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const contextMenuForm = () => <PlaylistsForm {...openForm!} />;

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        setSearchTerm((router?.query['searchTerm'] as string) || '');
    }, [router?.query]);

    const handleSearch = useCallback((e?: React.FormEvent<HTMLElement>, term?: string, selectSoundPage?: boolean) => {
        const termToUse = term || searchTerm;
        if (termToUse.trim()) {
            setShowComponent(false);
            localStorageAdd('search-history', termToUse);
            router.push({
                pathname: getCurrentPlatformLink('/search'),
                query: {
                    searchTerm: termToUse,
                    ...(selectSoundPage ? { type: 'albums' } : {})
                },
            });
            if (term) {
                setSearchTerm(term);
            }
        }
        e?.preventDefault();
    }, [router, searchTerm, setShowComponent]);

    useEffect(() => {
        setIsProfileMenuOpen(false);
        setShowNotifications(false);
    }, [router]);

    const handleMenuToggle = () => {
        closeFilters();
        setIsMaxiPlayer(false);
        if (isMobileViewPort) {
            closeNav();
        }
        setIsProfileMenuOpen(!isProfileMenuOpen);
    };

    useEffect(() => {
        setShowComponent(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query]);

    useEffect(() => {
        if (searchTerm) { setShowComponent(true); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const searchForm = (isMobile = false) => {
        if (isMobile) {
            return (
                <form className={classNames(styles['supreme-header--legacy-mobile__search'])}>
                    {/* @TODO setup react hook form */}
                    <div className={styles['supreme-header--legacy-mobile__search-container-input']}>
                        <input
                            type="text"
                            placeholder="Search artists, tracks, playlists and more."
                            name="searchTerm"
                            aria-label="Search Field"
                            autoComplete="off"
                            value={searchTerm}
                            className={styles['supreme-header--legacy-mobile__search-input']}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                            }}
                        />
                        <button
                            type="submit"
                            aria-label="Search"
                            className={styles['supreme-header--legacy-mobile__search-submit']}
                            onClick={(e) => {
                                handleSearch(e);
                            }}
                        >
                            {searchTerm?.length ? <SearchIconActive /> : <SearchIcon />}
                        </button>
                        {isMobile && isSearchPage && (
                            <button
                                onClick={() => (isFiltersOpen ? closeFilters() : openFilters())}
                                type="button"
                                aria-label={isFiltersOpen ? 'Close Filters' : 'Open Filters'}
                                className={styles['supreme-header--legacy-mobile__search-filters']}
                            >
                                {isFiltersOpen ? <i className="icon icon--controls-close" /> : <i className="icon icon--nav-filter" />}
                            </button>
                        )}
                        {!!searchTerm && (
                            <>
                                {isMobile ? <Filters platform={hub as Platform} rightAlign={DOWNLOAD_ONLY} inlineMobileStyle /> : null}
                                <button
                                    onClick={() => {
                                        hideSearchBar();
                                        setSearchTerm('');
                                    }}
                                    type="button"
                                    aria-label="Clear Search"
                                    className={styles['supreme-header--legacy-mobile__search-clear']}
                                >
                                    <i className="icon icon--controls-clear" />
                                </button>
                            </>
                        )}
                    </div>
                    {searchTerm && <PredictiveSearchMobile
                        term={searchTerm}
                        setTerm={(term) => {
                            if (term) {
                                handleSearch(undefined, term, true);
                            }
                        }} />}
                </form>
            );
        }
        return (
            <div ref={predictiveSearchRef}>
                <form className={styles['supreme-header__search']} onSubmit={handleSearch}>
                    <SearchInput
                        placeholder="Search artists, tracks, playlists and more."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                        }}
                        onClear={() => setSearchTerm('')}
                        variant="large"
                        onFocus={() => setShowComponent(true)}
                        isAlternateMobileLayout
                        isActive={showComponent}
                    />

                    <PredictiveSearch
                        term={searchTerm}
                        setTerm={(term) => {
                            if (term) {
                                handleSearch(undefined, term);
                            }
                        }}
                        setOpenForm={setOpenForm}
                        handleCancel={() => setShowComponent(false)}
                        isVisible={showComponent}
                    />
                </form>
            </div>

        );
    };

    return (
        <div className={styles['supreme-header']}>
            <ExpandNavButton
                onOpenNav={openNav}
                isMobile={isMobileViewPort}
                isNavHidden={isNavHidden}
                isNavOpen={isNavOpen}
            />
            {openForm && contextMenuForm()}
            <header className={classNames(styles['supreme-header__container'], { [styles['supreme-header__container--nav-opened']]: isNavOpen })}>
                <button
                    type="button"
                    aria-label="Open Nav"
                    onClick={() => {
                        if (isNavOpen) {
                            setShowNotifications(false);
                            closeNav();
                        } else {
                            openNav();
                        }
                    }}
                    className={styles['supreme-header__mobile-nav-btn']}
                >
                    {isNavOpen ? closeIcon : navIcon}
                </button>
                {isMobileViewPort ? (
                    <div className={styles['supreme-header__logo']}>
                        <AppLink href="/">
                            <a aria-label="Homepage" className={styles['supreme-header__logo-img']}>
                                <BPMLogo />
                            </a>
                        </AppLink>
                    </div>
                ) : null}
                <div className={styles['supreme-header__center']}>
                    <button
                        type="button"
                        disabled={!canGoBack}
                        className={classNames(styles['supreme-header__history'], styles['supreme-header__history--back'])}
                        aria-label="Previous Page"
                        onClick={goBack}
                    >
                        <i className="icon icon--chevron-left icon--light" />
                    </button>
                    <button
                        type="button"
                        disabled={!canGoForwards}
                        className={classNames(styles['supreme-header__history'], styles['supreme-header__history--forward'])}
                        aria-label="Next Page"
                        onClick={goForwards}
                    >
                        <i className="icon icon--chevron-right icon--light" />
                    </button>
                    {!isMobileViewPort && searchForm()}
                </div>
                <div className={styles['supreme-header__right']}>
                    <div>
                        <div ref={desktopSwitchPlatformOpenerRef}>
                            <HeaderPlatformsButton onClick={() => setShowPlatformSwitch(!showPlatformSwitch)} isOpen={showPlatformSwitch} />
                        </div>
                        <div ref={switchPlatformMenuRef}>
                            {showPlatformSwitch ? <PlatformSwitch
                                platforms={[
                                    { name: 'BPM Create' },
                                    { name: 'BPM Artist' },
                                ]}
                                onClose={() => {
                                    setShowPlatformSwitch(false);
                                }}
                            /> : null}
                        </div>
                    </div>
                    {isAnonymous ? (
                        <div className={styles['supreme-header__anonymous-buttons']}>
                            <button type="button" onClick={() => router.replace(getLoginRedirectLink())}>
                                <div>Log In</div>
                            </button>
                            <SquaredButton type="dynamic" label="Start Downloading" onPress={() => router.replace(getSignupRedirectLink())} />
                        </div>
                    ) : (
                        <>
                            <div ref={notificationElRef}>
                                <HeaderNotificationButton
                                    onClick={() => setShowNotifications((prevState) => !prevState)}
                                    hasNotifications={hasNotifications}
                                    isShowingNotifications={showNotifications}
                                />
                                <UserNotifications
                                    platform={isDownload ? Notification.PlatformEnum.Download : Notification.PlatformEnum.Stream}
                                    isVisible={showNotifications && !isMobileViewPort}
                                    onClose={() => setShowNotifications(false)}
                                />
                            </div>
                            <div ref={desktopMenuOpenerRef}>
                                <HeaderProfileButton
                                    isOpen={isProfileMenuOpen}
                                    isHidden={isAnonymous}
                                    imageAlt={userData?.data?.user?.full_name}
                                    imageUrl={userData?.data?.user?.profile_image_thumbnail_url}
                                    onClick={handleMenuToggle}
                                />
                            </div>
                        </>
                    )}
                </div>
                {isAnonymous ? (
                    <button
                        type="button"
                        aria-label="Open Nav"
                        className={styles['supreme-header__mobile-profile-btn']}
                        onClick={() => router.replace(getSignupRedirectLink())}
                    >
                        Sign Up
                    </button>
                ) : (
                    <HeaderProfileButton
                        imageAlt={userData?.data?.user?.full_name}
                        imageUrl={userData?.data?.user?.profile_image_thumbnail_url}
                        onClick={handleMenuToggle}
                        isMobile
                        isOpen={isProfileMenuOpen}
                        hasNotifications={hasNotifications}
                    />
                )}
            </header>
            <div className={classNames(styles['supreme-header__mobile'], { [styles['supreme-header__RNmobile']]: isWebview })}>
                <button
                    type="button"
                    aria-label="Home"
                    className={styles['supreme-header__mobile-btn']}
                    onClick={() => {
                        hideSearchBar();
                        router.push(getCurrentPlatformLink('/'));
                    }}
                >
                    <HomeIcon />
                </button>
                <button type="button" aria-label="Search" className={styles['supreme-header__mobile-btn']} onClick={() => isSearchVisible ? hideSearchBar() : showSearchBar()}>
                    <SearchIcon />
                </button>
                <AppLink href="/my-playlists">
                    <a aria-label="Playlists" className={styles['supreme-header__mobile-btn']}>
                        <PlaylistIcon />
                    </a>
                </AppLink>
            </div>
            <div
                ref={profileMenuRef}
                className={classNames(styles['supreme-header__user-menu'], {
                    [styles['supreme-header__user-menu--active']]: isProfileMenuOpen,
                })}
            >
                <div className={styles['supreme-header__user-menu-header']}>
                    <div className={styles['supreme-header__user-infos']}>
                        <div className={styles['supreme-header__user-image']}>
                            <HeaderProfileButton
                                onlyDisplayImage
                                isHidden={!isMobileViewPort}
                                imageAlt={userData?.data?.user?.full_name}
                                imageUrl={userData?.data?.user?.profile_image_thumbnail_url}
                            />
                        </div>
                        <div className={styles['supreme-header__user-info']}>
                            <div className={styles['supreme-header__user-name']}>{`${user?.first_name} ${user?.last_name}`}</div>
                            <div className={styles['supreme-header__user-id']}>{`Customer # ${user?.id}`}</div>
                        </div>
                        <div className={styles['supreme-header__notification-mobile-btn']}>
                            <HeaderNotificationButton
                                isMobile
                                onClick={() => setShowNotifications(!showNotifications)}
                                hasNotifications={hasNotifications}
                                isShowingNotifications={showNotifications}
                            />
                            <UserNotifications
                                platform={isDownload ? Notification.PlatformEnum.Download : Notification.PlatformEnum.Stream}
                                onClose={() => {
                                    setShowNotifications(false);
                                }}
                                isVisible={showNotifications && isMobileViewPort}
                                isMobile
                            />
                        </div>
                    </div>
                </div>
                <ProfileNavItems />
            </div>
            {isSearchVisible && (
                <div className={styles['supreme-header__mobile-search']}>
                    <div className={styles['supreme-header__mobile-search-form']}>{searchForm(true)}</div>
                </div>
            )}
            {showNotifications && <OnboardingModalNotifications />}
        </div>
    );
}

export default SupremeHeader;
